import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Drawer from '@/components/drawer/drawer';
import FactTile from '@/components/fact-tile/fact-tile';
import Loader from '@/components/loader/loader';
import SectionLabel from '@/components/section-label/section-label';
import StatusBadge from '@/components/status-badge/status-badge';
import Toggle from '@/components/toggle/toggle';
import {
  useLead,
  useUpdateLeadContactFlags,
  useUpdateLeadFlags,
} from '@/hooks/use-outbound-leads/use-outbound-leads';
import { OutboundCall } from '@/services/outbound-calls-service/calls.service.types';
import {
  Lead,
  LeadContact,
  NumberFlagsPayload,
} from '@/services/outbound-calls-service/leads.service.types';
import { openNotification } from '@/utils/notification/notification';
import { CallOutcome, CallResult, TransferOutcome } from '../call-result';
import { entryErrorLabel, outboundErrorMessage } from '../outbound-calls.messages';
import { ENTRY_STATUS_BADGE, campaignPath, formatDateTime } from '../outbound-calls.utils';
import styles from '../outbound-calls.module.scss';

interface LeadDrawerProps {
  leadId?: number;
  onClose: () => void;
}

const LEAD_FACTS = ['website', 'email', 'phone', 'address', 'account_owner'] as const;

const FACT_LABEL = {
  website: 'lead.website',
  email: 'lead.email',
  phone: 'lead.phone',
  address: 'lead.address',
  account_owner: 'lead.accountOwner',
} as const;

function CallAttempt({ call }: { call: OutboundCall }) {
  return (
    <li className={styles.listItem}>
      <div className={styles.row}>
        <span className={styles.stack}>
          <b>{formatDateTime(call.created_at)}</b>
          <span className={styles.secondary}>
            {[call.phone, call.campaign?.name, call.contact?.full_name].filter(Boolean).join(' · ')}
          </span>
        </span>
        <CallOutcome
          disposition={call.disposition}
          objectiveCompleted={call.objective_completed}
          live={call.status === 'live'}
        />
      </div>
      <TransferOutcome call={call} />
      <CallResult call={call} />
    </li>
  );
}

interface ContactRowProps {
  contact: LeadContact;
  pending: boolean;
  onChange: (flags: NumberFlagsPayload) => void;
}

function ContactRow({ contact, pending, onChange }: ContactRowProps) {
  const { t } = useTranslation('outbound');
  const name = contact.full_name || contact.phone;

  return (
    <li className={styles.listItem}>
      <div className={styles.row}>
        <span className={styles.stack}>
          <b>{name}</b>
          <span className={styles.secondary}>
            {[contact.position, contact.phone, contact.email].filter(Boolean).join(' · ')}
          </span>
        </span>
        <span className={styles.flags}>
          <span className={styles.flag}>
            <span className={styles.secondary}>{t('lead.doNotCall')}</span>
            <Toggle
              checked={contact.do_not_call}
              disabled={pending}
              label={t('lead.doNotCallContact', { name })}
              onChange={(checked) => onChange({ do_not_call: checked })}
            />
          </span>
          <span className={styles.flag}>
            <span className={styles.secondary}>{t('lead.wrongNumber')}</span>
            <Toggle
              checked={contact.wrong_number}
              disabled={pending}
              label={t('lead.wrongNumberContact', { name })}
              onChange={(checked) => onChange({ wrong_number: checked })}
            />
          </span>
        </span>
      </div>
    </li>
  );
}

function LeadBody({ lead }: { lead: Lead }) {
  const { t } = useTranslation('outbound');
  const { setLeadFlags, isPending: isLeadPending } = useUpdateLeadFlags(lead.id);
  const { setContactFlags, pendingContactId } = useUpdateLeadContactFlags(lead.id);
  const failed = (error: unknown) =>
    openNotification({ type: 'error', message: outboundErrorMessage(error, t('actionFailed')) });

  return (
    <>
      <div className={styles.listItem}>
        <div className={styles.flag}>
          <Toggle
            checked={lead.do_not_call}
            disabled={isLeadPending}
            label={t('lead.doNotCallLead')}
            onChange={(checked) => setLeadFlags({ do_not_call: checked }).catch(failed)}
          />
          <b>{t('lead.doNotCallLead')}</b>
        </div>
        <span className={styles.secondary}>{t('lead.doNotCallLeadHint')}</span>
      </div>

      {lead.phone && (
        <div className={styles.listItem}>
          <div className={styles.flag}>
            <Toggle
              checked={lead.wrong_number}
              disabled={isLeadPending}
              label={t('lead.wrongNumberLead')}
              onChange={(checked) => setLeadFlags({ wrong_number: checked }).catch(failed)}
            />
            <b>{t('lead.wrongNumberLead')}</b>
          </div>
          <span className={styles.secondary}>
            {t('lead.wrongNumberLeadHint', { phone: lead.phone })}
          </span>
        </div>
      )}

      <div className={styles.leadFacts}>
        {LEAD_FACTS.filter((fact) => lead[fact]).map((fact) => (
          <FactTile key={fact} label={t(FACT_LABEL[fact])} value={lead[fact]} />
        ))}
      </div>

      <SectionLabel>{t('lead.contacts')}</SectionLabel>
      {lead.contacts.length ? (
        <ul className={styles.list}>
          {lead.contacts.map((contact) => (
            <ContactRow
              key={contact.id}
              contact={contact}
              pending={pendingContactId === contact.id}
              onChange={(flags) => setContactFlags({ contactId: contact.id, flags }).catch(failed)}
            />
          ))}
        </ul>
      ) : (
        <p className={styles.secondary}>{t('lead.noContacts')}</p>
      )}

      {lead.entries.length > 0 && (
        <>
          <SectionLabel>{t('lead.campaigns')}</SectionLabel>
          <ul className={styles.list}>
            {lead.entries.map((entry) => (
              <li key={entry.id} className={styles.listItem}>
                <div className={styles.row}>
                  <span className={styles.stack}>
                    <Link to={campaignPath(entry.campaign.id)}>{entry.campaign.name}</Link>
                    <span className={styles.secondary}>
                      {entry.phone} · {t('lead.attemptsCount', { attempts: entry.attempts })}
                      {entry.callback_at &&
                        ` · ${t('entries.callbackAt', { at: formatDateTime(entry.callback_at) })}`}
                    </span>
                  </span>
                  <StatusBadge
                    variant={ENTRY_STATUS_BADGE[entry.status]}
                    label={t(
                      entry.callback_at ? 'entryStatus.callback' : `entryStatus.${entry.status}`
                    )}
                  />
                </div>
                {entry.last_error && (
                  <span className={styles.danger}>{entryErrorLabel(entry.last_error)}</span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      <SectionLabel>{t('lead.attempts')}</SectionLabel>
      {lead.calls.length ? (
        <ul className={styles.list}>
          {lead.calls.map((call) => (
            <CallAttempt key={call.id} call={call} />
          ))}
        </ul>
      ) : (
        <p className={styles.secondary}>{t('lead.noAttempts')}</p>
      )}
    </>
  );
}

function LeadDrawerContents({ leadId, onClose }: Required<LeadDrawerProps>) {
  const { t } = useTranslation('outbound');
  const { lead, error } = useLead(leadId);

  let body = <Loader />;
  if (lead) body = <LeadBody lead={lead} />;
  else if (error)
    body = <Alert type="error" showIcon message={outboundErrorMessage(error, t('errors.load'))} />;

  return (
    <Drawer
      open
      onClose={onClose}
      title={lead?.name ?? t('lead.fallbackTitle')}
      meta={
        lead && (
          <>
            {lead.nip && (
              <span>
                {t('lead.nip')} <b>{lead.nip}</b>
              </span>
            )}
            {lead.industry && (
              <span>
                {t('lead.industry')} <b>{lead.industry}</b>
              </span>
            )}
          </>
        )
      }
    >
      {body}
    </Drawer>
  );
}

/** A company's data, contacts, campaigns and every call made to it. */
export function LeadDrawer({ leadId, onClose }: LeadDrawerProps) {
  // Unmounted when closed, so a reopened panel never shows the previous company.
  if (!leadId) return null;
  return <LeadDrawerContents leadId={leadId} onClose={onClose} />;
}
